/* eslint-disable react/no-unescaped-entities */
import React, { useMemo, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, AlertTriangle, Clock, Info, Lock } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * DatePickerShowcase — unified design-system demos for the V4 DataAwareDatePicker.
 *
 * Replaces the previous standalone preview page (with its own topbar, theme
 * toggle, density toggle). Every demo here uses the real Orka design tokens
 * and renders inside the shared DSDemoFrame — so the date picker stops
 * feeling like a separate mini-app and instead slots cleanly into the
 * design system as one section.
 *
 * Exports one canonical component per demo so the design-system page can
 * compose them in its uniform layout:
 *
 *   <DatePickerTriggerVariants />
 *   <DatePickerCellMatrix />
 *   <DatePickerCalendar mode="loaded | loading | stale | error | locked" />
 *   <DatePickerGridSizes />
 *   <DatePickerDecisionBar />
 *
 * All demos share the same visible-grid rule: every cell — including
 * adjacent-month cells — is rendered identically.
 */

const EFFORT_BUCKETS = [
  { key: 'empty',      min: 0,  max: 0,   color: 'transparent', label: 'Empty',      meaning: 'nothing planned' },
  { key: 'light',      min: 1,  max: 15,  color: '#3B82F6',     label: 'Light',      meaning: 'light day' },
  { key: 'balanced',   min: 16, max: 25,  color: '#22C55E',     label: 'Balanced',   meaning: 'balanced day' },
  { key: 'heavy',      min: 26, max: 35,  color: '#F59E0B',     label: 'Heavy',      meaning: 'heavy day' },
  { key: 'overloaded', min: 36, max: 45,  color: '#EF4444',     label: 'Overloaded', meaning: 'overloaded' },
  { key: 'doom',       min: 46, max: 999, color: '#EF4444',     label: 'Doom',       meaning: 'strongly discourage' },
];

const SAMPLE_STATS = {
  '2026-01-26': { task_count: 1, total_effort: 5,  incomplete_count: 1, meeting_count: 0, personal_effort: 0, non_personal_effort: 5 },
  '2026-01-31': { task_count: 6, total_effort: 37, incomplete_count: 5, meeting_count: 2, personal_effort: 8, non_personal_effort: 29 },
  '2026-02-04': { task_count: 4, total_effort: 18, incomplete_count: 2, meeting_count: 1, personal_effort: 5, non_personal_effort: 13 },
  '2026-02-12': { task_count: 8, total_effort: 52, incomplete_count: 7, meeting_count: 2, personal_effort: 13, non_personal_effort: 39 },
  '2026-02-14': { task_count: 4, total_effort: 18, incomplete_count: 2, meeting_count: 1, personal_effort: 5, non_personal_effort: 13 },
  '2026-02-20': { task_count: 5, total_effort: 34, incomplete_count: 5, meeting_count: 0, personal_effort: 0, non_personal_effort: 34 },
  '2026-02-27': { task_count: 9, total_effort: 57, incomplete_count: 7, meeting_count: 4, personal_effort: 13, non_personal_effort: 44 },
  '2026-03-02': { task_count: 2, total_effort: 13, incomplete_count: 2, meeting_count: 1, personal_effort: 0, non_personal_effort: 13 },
  '2026-03-08': { task_count: 8, total_effort: 49, incomplete_count: 6, meeting_count: 2, personal_effort: 8, non_personal_effort: 41 },
  '2026-04-01': { task_count: 1, total_effort: 3,  incomplete_count: 1, meeting_count: 0, personal_effort: 0, non_personal_effort: 3 },
  '2026-05-29': { task_count: 7, total_effort: 46, incomplete_count: 6, meeting_count: 2, personal_effort: 5, non_personal_effort: 41 },
};

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function pad(n) { return String(n).padStart(2, '0'); }
function toISO(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function startOfMonth(y, m) { return new Date(y, m, 1); }
function endOfMonth(y, m) { return new Date(y, m + 1, 0); }
function startOfWeekMonday(d) { const day = d.getDay(); return addDays(d, day === 0 ? -6 : 1 - day); }
function endOfWeekMonday(d) { return addDays(startOfWeekMonday(d), 6); }
function buildVisibleGrid(y, m) {
  const start = startOfWeekMonday(startOfMonth(y, m));
  const end = endOfWeekMonday(endOfMonth(y, m));
  const out = [];
  for (let d = start; d <= end; d = addDays(d, 1)) out.push({ key: toISO(d), date: new Date(d) });
  return out;
}
function bucketFor(effort = 0) { return EFFORT_BUCKETS.find((b) => effort >= b.min && effort <= b.max) || EFFORT_BUCKETS[0]; }

/* ————————————————————————— ATOMS ———————————————————————————————————————— */

function EffortDot({ stats, state = 'loaded' }) {
  if (state === 'loading') {
    return <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-slate-200 via-slate-400 to-slate-200 animate-pulse" aria-hidden="true" />;
  }
  if (state === 'error') {
    return <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: '#EF4444' }} aria-hidden="true" />;
  }
  if (!stats || stats.total_effort === 0) {
    return <span className="inline-block w-2 h-2 rounded-full border orka-border-strong" aria-hidden="true" />;
  }
  const b = bucketFor(stats.total_effort);
  return <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: b.color }} aria-hidden="true" />;
}

function Tooltip({ date, stats, loading, pinned = false }) {
  const b = bucketFor(stats?.total_effort || 0);
  if (loading) {
    return (
      <div className={cn('rounded-xl border p-3 orka-border-strong orka-surface-raised w-[250px]', pinned && 'w-full')}>
        <div className="text-[11px] orka-text-tertiary font-bold mb-1">{date}</div>
        <div className="text-xs orka-text-secondary">Loading…</div>
      </div>
    );
  }
  return (
    <div className={cn('rounded-xl border orka-border-strong orka-surface-raised p-3 w-[250px]', pinned && 'w-full')} data-testid="ds-date-tooltip">
      <div className="text-[11px] orka-text-tertiary font-bold mb-2">{date}</div>
      {[
        ['Tasks', stats?.task_count || 0],
        ['Meetings', stats?.meeting_count || 0],
        ['Total Effort', `${stats?.total_effort || 0} · ${b.label}`],
        ['Personal', stats?.personal_effort || 0],
        ['Work', stats?.non_personal_effort || 0],
      ].map(([k, v], i) => (
        <div key={k} className={cn('flex justify-between text-[11px] py-1', i > 0 && 'border-t orka-border-subtle')}>
          <span className="orka-text-tertiary">{k}</span>
          <strong className="orka-text-primary" style={k === 'Total Effort' ? { color: b.color } : undefined}>{v}</strong>
        </div>
      ))}
    </div>
  );
}

function DayCell({ cell, selected, mode, onHover, onSelect }) {
  const stats = SAMPLE_STATS[cell.key];
  const weekend = [0, 6].includes(cell.date.getDay());
  const overloaded = (stats?.total_effort || 0) > 45;
  const missed = cell.key < '2026-02-14' && (stats?.incomplete_count || 0) > 0;
  const disabled = mode === 'locked';
  const loading = mode === 'loading';
  const stale = mode === 'stale';
  const error = mode === 'error';
  const today = cell.key === '2026-02-14';
  const label = `${cell.key} — ${loading ? 'loading day stats' : `${bucketFor(stats?.total_effort || 0).meaning}, ${stats?.task_count || 0} tasks`}`;
  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={label}
      onMouseEnter={() => onHover(cell.key)}
      onFocus={() => onHover(cell.key)}
      onClick={() => !disabled && onSelect(cell.key)}
      className={cn(
        'relative min-h-[46px] rounded-lg border orka-transition-colors flex flex-col items-center justify-start pt-1.5',
        'orka-border-transparent hover:-translate-y-px hover:orka-border-strong',
        weekend && !selected && 'orka-bg-info-soft/60',
        today && !selected && 'orka-bg-info-soft orka-border-strong font-bold',
        selected && 'orka-bg-intent orka-text-inverse font-bold',
        overloaded && !selected && 'orka-border-strong animate-pulse',
        stale && 'opacity-50 saturate-50',
        disabled && 'opacity-45 cursor-not-allowed orka-surface-subtle',
      )}
      style={missed ? { backgroundImage: 'repeating-linear-gradient(45deg,transparent 0 2px,rgba(156,163,175,.25) 2px 4px)' } : undefined}
    >
      <span className="text-[12px] font-extrabold leading-none">{cell.date.getDate()}</span>
      <span className="absolute bottom-1.5"><EffortDot stats={stats} state={error ? 'error' : loading ? 'loading' : 'loaded'} /></span>
    </button>
  );
}

/* ————————————————————————— DEMO COMPONENTS ——————————————————————————— */

export function DatePickerTriggerVariants() {
  const triggers = [
    { key: 'selected', icon: Calendar, text: '2026-02-14', badge: 'Selected' },
    { key: 'empty',    icon: Calendar, text: 'Pick a date', badge: 'Empty',    tone: 'empty' },
    { key: 'ghost',    icon: Calendar, text: '2026-03-02',  badge: 'Ghost',    tone: 'ghost' },
    { key: 'error',    icon: AlertTriangle, text: 'Stats unavailable', badge: 'Retry', tone: 'error' },
    { key: 'locked',   icon: Lock,     text: 'Tasks locked', badge: 'Access',  tone: 'locked' },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ds-date-triggers">
      {triggers.map(({ key, icon: Icon, text, badge, tone }) => (
        <button
          key={key}
          type="button"
          className={cn(
            'inline-flex items-center justify-between gap-3 w-full h-10 px-3 rounded-[15px] border orka-border-strong text-[13px] font-extrabold',
            tone === 'ghost' ? 'bg-transparent' : 'orka-surface-raised',
            tone === 'error' && 'orka-bg-danger-soft',
            tone === 'locked' && 'orka-bg-intent-soft',
            tone === 'empty' && 'orka-text-tertiary',
          )}
          data-testid={`ds-date-trigger-${key}`}
        >
          <span className="flex items-center gap-2 font-mono text-[13px]"><Icon className="w-4 h-4" />{text}</span>
          <span className="inline-flex items-center h-6 px-2 rounded-full border orka-border-subtle text-[10px]">{badge}</span>
        </button>
      ))}
    </div>
  );
}

export function DatePickerCellMatrix() {
  const cells = [
    { key: 'empty',       label: 'Empty',       day: 1,  props: {} },
    { key: 'loading',     label: 'Loading',     day: 2,  props: { loading: true } },
    { key: 'weekend',     label: 'Weekend',     day: 7,  props: { weekend: true,  stats: { total_effort: 8 } } },
    { key: 'today',       label: 'Today',       day: 14, props: { today: true,    stats: { total_effort: 18 } } },
    { key: 'selected',    label: 'Selected',    day: 14, props: { selected: true, stats: { total_effort: 18 } } },
    { key: 'missed',      label: 'Missed EOD',  day: 9,  props: { missed: true,   stats: { total_effort: 21 } } },
    { key: 'overloaded',  label: 'Overloaded',  day: 27, props: { overloaded: true, stats: { total_effort: 57 } } },
    { key: 'stale',       label: 'Stale',       day: 20, props: { stale: true,    stats: { total_effort: 34 } } },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ds-date-cell-matrix">
      {cells.map((c) => (
        <div key={c.key} className="rounded-xl border orka-border-subtle orka-surface-base p-3">
          <div className="text-[10px] uppercase tracking-wider orka-text-tertiary font-bold mb-2">{c.label}</div>
          <div className="flex justify-center">
            <div
              className={cn(
                'relative h-12 w-12 rounded-lg border orka-transition-colors flex flex-col items-center justify-start pt-1.5',
                'orka-border-subtle',
                c.props.weekend && 'orka-bg-info-soft/60',
                c.props.today && 'orka-bg-info-soft orka-border-strong font-bold',
                c.props.selected && 'orka-bg-intent orka-text-inverse font-bold',
                c.props.overloaded && 'orka-border-strong animate-pulse',
                c.props.stale && 'opacity-50 saturate-50',
              )}
              style={c.props.missed ? { backgroundImage: 'repeating-linear-gradient(45deg,transparent 0 2px,rgba(156,163,175,.25) 2px 4px)' } : undefined}
            >
              <span className="text-[12px] font-extrabold leading-none">{c.day}</span>
              <span className="absolute bottom-1.5"><EffortDot stats={c.props.stats} state={c.props.loading ? 'loading' : 'loaded'} /></span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function DatePickerCalendar({ year = 2026, monthIndex = 1, mode = 'loaded', pinnedTooltip = false }) {
  const cells = useMemo(() => buildVisibleGrid(year, monthIndex), [year, monthIndex]);
  const defaultSel = monthIndex === 1 ? '2026-02-14' : cells[Math.floor(cells.length / 2)].key;
  const [selected, setSelected] = useState(defaultSel);
  const [hovered, setHovered] = useState(defaultSel);
  const monthName = new Date(year, monthIndex, 1).toLocaleString('en', { month: 'long', year: 'numeric' });
  const rangeStart = cells[0].key;
  const rangeEnd = cells[cells.length - 1].key;
  const locked = mode === 'locked';
  const error = mode === 'error';
  const stale = mode === 'stale';
  const loading = mode === 'loading';

  return (
    <div className="rounded-2xl border orka-border-strong orka-surface-raised p-4" data-testid="ds-date-calendar" data-mode={mode}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-sm font-extrabold orka-text-primary">{monthName}</div>
          <div className="text-[10px] uppercase tracking-wider orka-text-tertiary mt-0.5 font-mono">
            Request: {rangeStart} → {rangeEnd}
          </div>
        </div>
        <div className="flex gap-1">
          <button type="button" className="h-7 w-7 rounded-md flex items-center justify-center border orka-border-subtle hover:orka-surface-subtle" aria-label="Previous"><ChevronLeft className="w-3.5 h-3.5" /></button>
          <button type="button" className="h-7 w-7 rounded-md flex items-center justify-center border orka-border-subtle hover:orka-surface-subtle" aria-label="Next"><ChevronRight className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      {locked && <div className="mb-2 inline-flex items-center gap-2 px-2 py-1 rounded-md orka-bg-intent-soft border orka-border-strong text-[11px] font-bold"><Lock className="w-3 h-3" /> Tasks module locked</div>}
      {error && <div className="mb-2 inline-flex items-center gap-2 px-2 py-1 rounded-md orka-bg-danger-soft border orka-border-strong text-[11px] font-bold"><AlertTriangle className="w-3 h-3" /> Couldn't load stats — retry</div>}
      {stale && <div className="mb-2 inline-flex items-center gap-2 px-2 py-1 rounded-md orka-bg-warning-soft border orka-border-strong text-[11px] font-bold"><Clock className="w-3 h-3" /> Refreshing visible grid stats…</div>}

      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS.map((w) => (
          <div key={w} className="h-5 flex items-center justify-center text-[9px] font-extrabold uppercase tracking-wider orka-text-tertiary">{w}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((cell) => (
          <DayCell
            key={cell.key}
            cell={cell}
            selected={cell.key === selected}
            mode={mode}
            onHover={setHovered}
            onSelect={setSelected}
          />
        ))}
      </div>

      {!locked && (
        <div className="mt-3">
          <Tooltip date={hovered} stats={SAMPLE_STATS[hovered]} loading={loading} pinned={pinnedTooltip} />
        </div>
      )}
    </div>
  );
}

export function DatePickerGridSizes({ mode = 'loaded' }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="ds-date-grid-sizes">
      <div>
        <div className="text-[10px] uppercase tracking-wider orka-text-tertiary font-bold mb-2">7 × 5 grid</div>
        <DatePickerCalendar year={2026} monthIndex={1} mode={mode} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider orka-text-tertiary font-bold mb-2">7 × 5 (April)</div>
        <DatePickerCalendar year={2026} monthIndex={3} mode={mode} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider orka-text-tertiary font-bold mb-2">7 × 6 (May)</div>
        <DatePickerCalendar year={2026} monthIndex={4} mode={mode} />
      </div>
    </div>
  );
}

export function DatePickerEffortLegend() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="ds-date-effort-legend">
      {EFFORT_BUCKETS.map((b) => (
        <div key={b.key} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border orka-border-subtle orka-surface-raised">
          <div>
            <div className="text-[12px] font-bold orka-text-primary">{b.label}</div>
            <div className="text-[10px] orka-text-tertiary">{b.min === b.max ? '0' : `${b.min}–${b.max === 999 ? '∞' : b.max}`} effort · {b.meaning}</div>
          </div>
          <span className="w-2.5 h-2.5 rounded-full border" style={{ backgroundColor: b.color === 'transparent' ? 'transparent' : b.color, borderColor: b.color === 'transparent' ? 'var(--orka-border-default)' : 'transparent' }} aria-hidden="true" />
        </div>
      ))}
    </div>
  );
}

export function DatePickerDecisionBar() {
  return (
    <div className="rounded-xl border orka-border-strong orka-bg-warning-soft p-4 flex flex-wrap items-center justify-between gap-3" data-testid="ds-date-decision-bar">
      <div>
        <strong className="text-[13px] font-extrabold">This day is already at 57 effort points.</strong>
        <div className="text-[11px] orka-text-tertiary mt-0.5">Strongly discourage adding more unless this is genuinely urgent.</div>
      </div>
      <div className="flex gap-2">
        <button type="button" className="h-9 px-3 rounded-lg border orka-border-subtle orka-surface-raised text-[12px] font-extrabold">Choose another day</button>
        <button type="button" className="h-9 px-3 rounded-lg border orka-border-strong orka-bg-warning-soft text-[12px] font-extrabold">Continue anyway</button>
      </div>
    </div>
  );
}
